import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useUsers, useCreateUser, useUpdateUser } from "../api/use-users";
import type {
  CreateUserRequest,
  UpdateUserRequest,
  UserListItem,
} from "../types";
import { CreateUserDialog } from "./create-user-dialog";
import { EditUserDialog } from "./edit-user-dialog";
import { TempPasswordDialog } from "./temp-password-dialog";
import { UserInitialsAvatar } from "./user-initials-avatar";
import { RoleBadge } from "./role-badge";
import { StatusBadge } from "./status-badge";

export function UsersPage() {
  const { data: users = [], isLoading } = useUsers();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserListItem | null>(null);
  const [tempPassword, setTempPassword] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.fullName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q),
    );
  }, [users, search]);

  const handleCreate = async (data: CreateUserRequest) => {
    setCreateError(null);
    try {
      const result = await createUser.mutateAsync(data);
      setCreateOpen(false);
      setTempPassword({
        email: data.email,
        password: result.temporaryPassword,
      });
      toast.success("User created");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to create user";
      setCreateError(message);
    }
  };

  const handleUpdate = async (data: UpdateUserRequest) => {
    if (!editingUser) return;
    setEditError(null);
    try {
      await updateUser.mutateAsync({ id: editingUser.id, data });
      setEditingUser(null);
      toast.success("User updated");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update user";
      setEditError(message);
    }
  };

  const columns: Column<UserListItem>[] = [
    {
      key: "fullName",
      header: "Name",
      render: (row) => (
        <div className="flex items-center gap-2.5">
          <UserInitialsAvatar
            firstName={row.firstName}
            lastName={row.lastName}
          />
          <span className="font-medium text-white">{row.fullName}</span>
        </div>
      ),
    },
    {
      key: "email",
      header: "Email",
      render: (row) => <span className="text-text-soft">{row.email}</span>,
    },
    {
      key: "role",
      header: "Role",
      render: (row) => <RoleBadge role={row.role} />,
    },
    {
      key: "isActive",
      header: "Status",
      render: (row) => <StatusBadge isActive={row.isActive} />,
    },
    {
      key: "edit",
      header: "Edit",
      hideHeader: true,
      sortable: false,
      align: "right",
      render: (row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setEditError(null);
            setEditingUser(row);
          }}
          data-testid={`edit-user-${row.id}`}
          className="text-text-faint hover:text-text-soft text-sm"
        >
          Edit
        </button>
      ),
    },
  ];

  return (
    <div className="animate-fade-in">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Users</h1>
          <p className="text-sm text-text-muted mt-1">
            Manage who can access Butler Tech schedules.
          </p>
        </div>
        <Button
          onClick={() => {
            setCreateError(null);
            setCreateOpen(true);
          }}
          data-testid="add-user-button"
        >
          Add user
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-3.5">
        <div className="relative flex-1 max-w-[320px]">
          <svg
            aria-hidden="true"
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-faint"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <Input
            type="search"
            placeholder="Search users…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-surface pl-8 py-1.5 text-[13px]"
            aria-label="Search users"
            data-testid="users-search-input"
          />
        </div>
        <span data-testid="users-count" className="text-xs text-text-muted">
          {users.length} {users.length === 1 ? "user" : "users"}
        </span>
      </div>

      <div className="bg-surface border border-border-subtle rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="py-12 flex justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={filteredUsers}
            getRowKey={(row) => row.id}
            emptyMessage={
              search ? "No users match your search." : "No users yet."
            }
          />
        )}
      </div>

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        isLoading={createUser.isPending}
        error={createError}
      />

      {editingUser && (
        <EditUserDialog
          open
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSubmit={handleUpdate}
          isLoading={updateUser.isPending}
          error={editError}
        />
      )}

      {tempPassword && (
        <TempPasswordDialog
          open
          email={tempPassword.email}
          password={tempPassword.password}
          onClose={() => setTempPassword(null)}
        />
      )}
    </div>
  );
}
